import { FC, useId, useState } from "react";
import { Box, Checkbox, Dialog, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Button } from "src/domains/shared/components/Button";
import { ModalHeader } from "src/domains/shared/components/ModalHeader";
import { COLOR_CONSTANTS } from "src/domains/shared/constants/styles";
import { BusyLabel, CAPTION, FIELD_DISABLED_BG, LineGlyph } from "src/domains/shared/components/StateTransition";

interface WithdrawDialogProps {
  open: boolean;
  processing: boolean;
  /** 탈퇴 거절·실패 안내 — 서버 문구(409)·재시도 안내·일반 실패. 창 안에 남겨 재시도 경로를 준다. */
  errorText?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

const DIALOG_WIDTH_PX = 400;
const TERMS_HEIGHT_PX = 168;
const CONFIRM_MIN_WIDTH_PX = 76;

/**
 * 회원탈퇴 확인 창 — 누르기 전에 소유 프로젝트 조건과 기록 보존("(알 수 없음)" 표시)을 약관 상자 **밖**에서
 * 미리 알리고, 약관은 고정 높이 상자 안에서만 스크롤하며, 동의 전에는 탈퇴하기가 잠긴다.
 */
export const WithdrawDialog: FC<WithdrawDialogProps> = ({ open, processing, errorText = null, onCancel, onConfirm }) => {
  const { t } = useTranslation(["projects", "common"]);
  const titleId = useId();
  const agreeId = useId();
  const [agreed, setAgreed] = useState(false);

  const close = () => {
    if (processing) return;
    setAgreed(false);
    onCancel();
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      aria-labelledby={titleId}
      PaperProps={{ sx: { width: DIALOG_WIDTH_PX, maxWidth: "calc(100vw - 32px)", borderRadius: "8px" } }}
    >
      <ModalHeader title={t("myPage.withdrawal.title", { ns: "projects" })} titleId={titleId} size="small" onClose={close} />
      <Box sx={{ padding: "14px 20px", display: "flex", flexDirection: "column", gap: "12px" }}>
        <Box sx={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: COLOR_CONSTANTS.error._states.selected,
              color: COLOR_CONSTANTS.error.main,
            }}
          >
            <LineGlyph name="alert" />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 500, lineHeight: 1.45 }}>
              {t("myPage.messages.confirmWithdrawal", { ns: "projects" })}
            </Typography>
            <Typography sx={{ fontSize: 11, lineHeight: 1.4, color: CAPTION }}>
              {t("myPageModal.withdrawal.description", { ns: "projects" })}
            </Typography>
          </Box>
        </Box>
        {/* 사전 안내 — 스크롤 상자 밖이라 누르기 전에 반드시 보인다. */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", fontSize: 12, lineHeight: 1.5, color: COLOR_CONSTANTS.text.secondary }}>
          <Box component="p" sx={{ margin: 0 }}>{t("myPageModal.withdrawal.ownedProjectNotice", { ns: "projects" })}</Box>
          {/* 보존 안내는 탈퇴한 작성자 라벨을 인용한다 — 라벨의 단일 키(`withdrawn-user-display`)를 보간해 두 문구가 어긋나지 않게 한다. */}
          <Box component="p" sx={{ margin: 0 }}>
            {t("myPageModal.withdrawal.retentionNotice", {
              ns: "projects",
              unknownLabel: t("user.unknown", { ns: "common" }),
            })}
          </Box>
        </Box>
        <Box
          tabIndex={0}
          role="region"
          aria-label={t("myPage.quitAgreement", { ns: "common" })}
          sx={{
            height: TERMS_HEIGHT_PX,
            overflowY: "auto",
            padding: "12px",
            borderRadius: "8px",
            border: "1px solid rgba(0, 0, 0, 0.08)",
            backgroundColor: FIELD_DISABLED_BG,
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            fontSize: 12,
            lineHeight: 1.6,
            color: COLOR_CONSTANTS.text.secondary,
          }}
        >
          <Typography component="h3" sx={{ fontSize: 12, fontWeight: 600, color: COLOR_CONSTANTS.text.primary }}>
            {t("myPage.quitAgreement", { ns: "common" })}
          </Typography>
          <Box component="p" sx={{ margin: 0 }}>{t("myPage.quitAggrementDescription.first", { ns: "common" })}</Box>
          <Box component="p" sx={{ margin: 0 }}>{t("myPage.quitAggrementDescription.second", { ns: "common" })}</Box>
          <Box component="p" sx={{ margin: 0 }}>{t("myPage.quitAggrementDescription.third", { ns: "common" })}</Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minHeight: 28 }}>
          <Checkbox
            id={agreeId}
            size="small"
            checked={agreed}
            disabled={processing}
            onChange={(event) => setAgreed(event.target.checked)}
            sx={{ padding: 0 }}
          />
          <Typography component="label" htmlFor={agreeId} sx={{ fontSize: 13, cursor: "pointer" }}>
            {t("myPage.quitAggrementDescription.fourth", { ns: "common" })}
          </Typography>
        </Box>
        {errorText && (
          <Typography role="alert" sx={{ fontSize: 12, lineHeight: 1.45, color: COLOR_CONSTANTS.error.main }}>
            {errorText}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "8px", padding: "0 20px 16px" }}>
        <Button size="small" emphasis="outlined" intent="inherit" disabled={processing} onClick={close}>
          {t("myPage.withdrawal.cancel", { ns: "projects" })}
        </Button>
        <Button
          size="small"
          emphasis="contained"
          intent="error"
          disabled={!agreed}
          onClick={processing ? undefined : onConfirm}
          sx={{ minWidth: CONFIRM_MIN_WIDTH_PX }}
        >
          <BusyLabel
            label={t("myPage.withdrawal.confirm", { ns: "projects" })}
            busy={processing}
            busyLabel={t("myPageModal.withdrawal.processing", { ns: "projects" })}
            spinnerSize={12}
          />
        </Button>
      </Box>
    </Dialog>
  );
};
